import { useNavigate, Link } from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import { openSupportChat } from "@/utils/openSupportChat";
import { Helmet } from "react-helmet-async";
import {
  FileText,
  CheckCircle,
  Shield,
  Clock,
  ArrowRight,
  Star,
  Download,
  Building2,
  User,
  DollarSign,
  Calendar,
  Calculator,
  ChevronDown,
  ChevronUp,
  FileBarChart,
  Receipt,
  Briefcase,
  HelpCircle,
  Zap,
  MousePointer,
  Edit3,
  Expand,
  Loader2
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import select from '../assests/select.png';
import inputInfo from '../assests/inputInfo.png';
import download from '../assests/download.png';
import { loadJsPDF } from "@/utils/pdfEngines";
import { generateTemplateA, generateTemplateC, generateTemplateH } from "@/utils/paystubTemplates";
import { loadPdfJs } from "@/utils/pdfEngines";

// Set up pdf.js worker

// Same reveal hook the home page uses (tiny threshold so tall sections
// still trigger on mobile).
const useInView = (options = {}) => {
  const ref = useRef(null);
  const [isInView, setIsInView] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setIsInView(true);
        observer.disconnect();
      }
    }, { threshold: 0.05, ...options });
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []); // eslint-disable-line
  return [ref, isInView];
};

const toneCard = {
  mint: { bg: "bg-emerald-400", big: "text-slate-900", small: "text-emerald-950" },
  black: { bg: "bg-[#0b0b0b]", big: "text-white", small: "text-slate-300" },
  gray: { bg: "bg-slate-100", big: "text-slate-900", small: "text-slate-600" },
};

const HERO_STATS = [
  { value: "5 min", label: "Average Time", tone: "mint" },
  { value: "10K+", label: "Paystubs Created", tone: "black" },
  { value: "99.9%", label: "Accuracy Rate", tone: "gray" },
];

// Sample data for generating previews
const SAMPLE_DATA = {
  company: "MintSlip",
  companyAddress: "123 Financial Way",
  companyCity: "Austin",
  companyState: "TX",
  companyZip: "78701",
  companyPhone: "(512) 555-0100",
  ein: "12-3456789",
  name: "John Doe",
  address: "456 Oak Street",
  city: "Austin",
  state: "TX",
  zip: "78702",
  employeeId: "1234",
  ssn: "1234",
  bank: "1234",
  payFrequency: "biweekly",
  rate: 25.00,
  hours: 80,
  overtime: 0,
  commission: 0,
  bonus: 0,
  workerType: "employee",
  payType: "hourly",
  annualSalary: 52000,
  hireDate: "2023-06-15",
  periodStart: "2024-12-01",
  periodEnd: "2024-12-14",
  payDate: "2024-12-20",
  federalFilingStatus: "single",
  stateAllowances: "1",
  includeLocalTax: false,
  maritalStatus: "single",
  federalExemptions: "1",
  stateExemptions: "1",
  companyCode: "MINT01",
  locDept: "100/ADM",
  checkNumber: "1001",
  pageNumber: "1",
  bankName: "Mint Bank",
  bankLast4: "1234",
  routingNumber: "021000021",
  accountType: "Checking",
  positionTitle: "Staff Accountant",
  department: "Finance",
  costCenter: "1000",
  deductions: [],
  contributions: [],
  absencePlans: [],
  employerBenefits: [],
};

// Template configurations
const TEMPLATES = [
  {
    id: "template-a",
    name: "Gusto Style",
    description: "Clean, modern paystub design inspired by Gusto payroll. Features a compact layout with clear earnings breakdown.",
    features: ["Modern clean design", "Compact single-page layout", "Clear earnings breakdown", "Employee & employer info sections"],
    generator: generateTemplateA,
    color: "#F4A460",
  },
  // {
  //   id: "template-b",
  //   name: "ADP Style",
  //   description: "Professional enterprise-style paystub matching ADP's Earnings Statement format with detailed tax sections.",
  //   features: ["Enterprise professional format", "Detailed tax breakdown", "Marital status & exemptions", "Check stub with bank info"],
  //   generator: generateTemplateB,
  //   color: "#d0271d",
  // },
  {
    id: "template-c",
    name: "Workday Style",
    description: "Corporate HR-focused paystub design inspired by Workday. Features absence plan tracking and employer benefits.",
    features: ["Corporate HR style", "Absence plan tracking", "Employer benefits section", "Side-by-side tables"],
    generator: generateTemplateC,
    color: "#0066cc",
  },
  {
    id: "template-h",
    name: "OnPay Style",
    description: "Clean, modern paystub design inspired by OnPay payroll. Features a compact layout with clear earnings breakdown, tax withholdings, and YTD totals. Perfect for small to medium businesses.",
    features: ["Colorful modern design", "Three-column layout", "Direct deposit emphasis", "Accruals & ACH sections"],
    generator: generateTemplateH,
    color: "#000", // Orange accent
  },
];

// Convert PDF to image
async function convertPdfToImage(pdfDataUrl, scale = 1.5) {
  const base64Data = pdfDataUrl.split(',')[1];
  const binaryString = atob(base64Data);
  const len = binaryString.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }

  const loadingTask = (await loadPdfJs()).getDocument({ data: bytes.buffer });
  const pdf = await loadingTask.promise;
  const page = await pdf.getPage(1);
  const viewport = page.getViewport({ scale });

  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');
  canvas.width = viewport.width;
  canvas.height = viewport.height;

  await page.render({
    canvasContext: context,
    viewport: viewport
  }).promise;

  return canvas.toDataURL('image/png', 0.9);
}

// Generate MintSlip logo
function generateMintSlipLogo() {
  const canvas = document.createElement('canvas');
  canvas.width = 200;
  canvas.height = 60;
  const ctx = canvas.getContext('2d');
  ctx.clearRect(0, 0, canvas.width, canvas.height);
  ctx.font = 'bold 36px "Segoe UI", Arial, sans-serif';
  ctx.fillStyle = '#1a4731';
  ctx.textBaseline = 'middle';
  ctx.fillText('MintSlip', 10, 32);
  return canvas.toDataURL('image/png');
}

// Generate sample preview for a template
async function generateSamplePreview(template) {
  const doc = new (await loadJsPDF())({
    orientation: "portrait",
    unit: "pt",
    format: "letter",
  });

  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 40;
  const mintSlipLogo = generateMintSlipLogo();

  const rate = SAMPLE_DATA.rate;
  const hours = SAMPLE_DATA.hours;
  const overtime = SAMPLE_DATA.overtime;
  const regularPay = rate * hours;
  const overtimeRate = rate * 1.5;
  const overtimePay = overtimeRate * overtime;
  const grossPay = regularPay + overtimePay;

  const ssTax = grossPay * 0.062;
  const medTax = grossPay * 0.0145;
  const federalTax = grossPay * 0.12;
  const stateTax = 0;
  const localTax = 0;
  const totalTax = ssTax + medTax + federalTax + stateTax + localTax;
  const netPay = grossPay - totalTax;

  const ytdMultiplier = 6;
  const startDate = new Date(SAMPLE_DATA.periodStart);
  const endDate = new Date(SAMPLE_DATA.periodEnd);
  const payDate = new Date(SAMPLE_DATA.payDate);

  const templateData = {
    formData: { ...SAMPLE_DATA },
    rate, hours, overtime, overtimeRate, regularPay, overtimePay, grossPay,
    ssTax, medTax, federalTax, stateTax, localTax, totalTax, netPay,
    startDate, endDate, payDate,
    payFrequency: SAMPLE_DATA.payFrequency,
    stubNum: 1, totalStubs: 1,
    ytdRegularPay: regularPay * ytdMultiplier,
    ytdOvertimePay: overtimePay * ytdMultiplier,
    ytdGrossPay: grossPay * ytdMultiplier,
    ytdSsTax: ssTax * ytdMultiplier,
    ytdMedTax: medTax * ytdMultiplier,
    ytdFederalTax: federalTax * ytdMultiplier,
    ytdStateTax: stateTax * ytdMultiplier,
    ytdLocalTax: 0,
    ytdTotalTax: totalTax * ytdMultiplier,
    ytdNetPay: netPay * ytdMultiplier,
    ytdHours: hours * ytdMultiplier,
    ytdOvertimeHours: overtime * ytdMultiplier,
    ytdCommission: 0, ytdBonus: 0, ytdPayPeriods: ytdMultiplier,
    commission: 0, bonus: 0,
    deductionsData: [], contributionsData: [], absencePlansData: [], employerBenefitsData: [],
    totalDeductions: 0, totalContributions: 0,
    preTaxDeductions: 0, postTaxDeductions: 0,
    preTaxContributions: 0, postTaxContributions: 0,
    totalPreTax: 0, totalPostTax: 0,
    ytdDeductions: 0, ytdContributions: 0,
    payType: SAMPLE_DATA.payType,
    annualSalary: SAMPLE_DATA.annualSalary,
    workerType: SAMPLE_DATA.workerType,
    isContractor: false,
    logoDataUrl: mintSlipLogo,
    stateRate: 0, localTaxRate: 0, sutaRate: 0,
  };

  await template.generator(doc, templateData, pageWidth, pageHeight, margin);
  const pdfDataUrl = doc.output('datauristring');
  return await convertPdfToImage(pdfDataUrl, 2);
}

// FAQ Accordion Component
const FAQItem = ({ question, answer, isOpen, onClick }) => (
  <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden mb-3">
    <button
      onClick={onClick}
      className="w-full flex items-center justify-between p-5 text-left hover:bg-slate-50 transition-colors"
    >
      <span className="font-semibold text-slate-900 pr-4">{question}</span>
      {isOpen ? (
        <ChevronUp className="w-5 h-5 text-emerald-600 flex-shrink-0" />
      ) : (
        <ChevronDown className="w-5 h-5 text-slate-400 flex-shrink-0" />
      )}
    </button>
    {isOpen && (
      <div className="px-5 pb-5">
        <p className="text-slate-600 leading-relaxed">{answer}</p>
      </div>
    )}
  </div>
);

// Internal Link Card Component
const InternalLinkCard = ({ title, description, path, icon: Icon }) => {
  const navigate = useNavigate();
  return (
    <div
      onClick={() => navigate(path)}
      className="group bg-white border border-slate-200 rounded-2xl shadow-sm p-5 cursor-pointer hover:border-emerald-400 hover:shadow-md transition-all duration-300"
    >
      <div className="flex items-start gap-4">
        <div className="w-12 h-12 rounded-full bg-emerald-500 flex items-center justify-center flex-shrink-0">
          <Icon className="w-6 h-6 text-white" />
        </div>
        <div className="flex-1">
          <h4 className="font-display font-bold text-slate-900 group-hover:text-emerald-700 transition-colors mb-1">{title}</h4>
          <p className="text-sm text-slate-500">{description}</p>
        </div>
        <ArrowRight className="w-5 h-5 text-slate-300 group-hover:text-emerald-600 group-hover:translate-x-1 transition-all" />
      </div>
    </div>
  );
};

// Template Preview Card with actual PDF preview
const TemplatePreviewCard = ({ template, previewImage, isLoading, onUseTemplate }) => {
  const [dialogOpen, setDialogOpen] = useState(false);

  return (
    <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm hover:shadow-lg transition-all duration-300">
      {/* Preview Image */}
      <div className="relative aspect-[8.5/11] bg-slate-100 overflow-hidden">
        {isLoading ? (
          <div className="absolute inset-0 flex items-center justify-center">
            <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
          </div>
        ) : previewImage ? (
          <>
            <img
              src={previewImage}
              alt={`${template.name} preview`}
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-black/50 to-transparent" />
          </>
        ) : (
          <div className="absolute inset-0 flex items-center justify-center">
            <FileText className="w-16 h-16 text-slate-300" />
          </div>
        )}

        {/* Template name badge */}
        <div
          className="absolute top-3 left-3 px-3 py-1.5 rounded-full text-white text-sm font-semibold shadow-lg"
          style={{ backgroundColor: template.color }}
        >
          {template.name}
        </div>

        {/* Expand button */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <button
              className="absolute top-3 right-3 p-2 bg-white/90 hover:bg-white rounded-full shadow-lg transition-colors"
              disabled={isLoading || !previewImage}
            >
              <Expand className="w-4 h-4 text-slate-700" />
            </button>
          </DialogTrigger>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <span
                  className="w-3 h-3 rounded-full"
                  style={{ backgroundColor: template.color }}
                />
                {template.name} Preview
              </DialogTitle>
            </DialogHeader>
            {previewImage && (
              <div className="mt-4">
                <img
                  src={previewImage}
                  alt={`${template.name} full preview`}
                  className="w-full rounded-lg border border-slate-200 shadow-lg"
                />
              </div>
            )}
            <div className="mt-4 flex justify-end">
              <Button
                onClick={() => {
                  setDialogOpen(false);
                  onUseTemplate(template.id);
                }}
                className="rounded-full bg-emerald-600 hover:bg-emerald-700"
              >
                Use This Template
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Card Content */}
      <div className="p-5">
        <h3 className="font-display text-lg font-bold text-slate-900 mb-2">
          {template.name}
        </h3>
        <p className="text-sm text-slate-600 mb-4 line-clamp-2">
          {template.description}
        </p>

        {/* Features list */}
        <ul className="space-y-1.5 mb-5">
          {template.features.map((feature, idx) => (
            <li key={idx} className="flex items-center gap-2 text-sm text-slate-600">
              <CheckCircle className="w-4 h-4 text-emerald-600 flex-shrink-0" />
              {feature}
            </li>
          ))}
        </ul>

        {/* Action Button */}
        <Button
          onClick={() => onUseTemplate(template.id)}
          className="w-full rounded-full bg-emerald-600 hover:bg-emerald-700 text-white"
        >
          Use This Template
          <ArrowRight className="w-4 h-4 ml-2" />
        </Button>
      </div>
    </div>
  );
};

export default function HowToMakePaystub() {
  const navigate = useNavigate();
  const [openFAQ, setOpenFAQ] = useState(0);
  const [previews, setPreviews] = useState({});
  const [loading, setLoading] = useState({});
  const userCount = "1,000+"; // hardcoded hero stat

  const [heroRef, heroInView] = useInView();
  const [stepsRef, stepsInView] = useInView();
  const [templatesRef, templatesInView] = useInView();
  const [faqRef, faqInView] = useInView();
  const [linksRef, linksInView] = useInView();
  const [ctaRef, ctaInView] = useInView();

  // Generate previews on mount
  useEffect(() => {
    const generateAllPreviews = async () => {
      for (const template of TEMPLATES) {
        setLoading(prev => ({ ...prev, [template.id]: true }));
        try {
          const preview = await generateSamplePreview(template);
          setPreviews(prev => ({ ...prev, [template.id]: preview }));
        } catch (error) {
          console.error(`Error generating preview for ${template.name}:`, error);
        }
        setLoading(prev => ({ ...prev, [template.id]: false }));
      }
    };

    generateAllPreviews();
  }, []);

  const handleUseTemplate = () => {
    navigate("/app/paystubs");
  };

  const faqs = [
    {
      question: "What information do I need to create a paystub?",
      answer: "To create an accurate paystub, you&apos;ll need: your employer&apos;s name and address, employee name and details, pay period dates, hourly rate or salary, hours worked, federal and state tax information, and any deductions like insurance or retirement contributions. Our generator guides you through each field step by step."
    },
    {
      question: "How long does it take to generate a paystub with MintSlip?",
      answer: "Most users complete their paystub in under 5 minutes. Our streamlined interface and auto-calculation features handle the complex math for taxes and deductions, so you just enter your basic information and we do the rest."
    },
    {
      question: "Are MintSlip paystubs accurate for tax purposes?",
      answer: "Yes! MintSlip uses current federal and state tax rates to calculate accurate withholdings. Our templates follow standard payroll formatting used by major employers, ensuring your paystub contains all the information typically found on professional pay stubs."
    },
    {
      question: "Can I create paystubs for self-employment or contract work?",
      answer: "Absolutely! MintSlip offers both W-2 employee paystubs and 1099 contractor payment statements. For self-employed individuals, our system can generate accurate documentation of your earnings without tax withholdings, as is standard for independent contractors."
    },
    {
      question: "What paystub template formats are available?",
      answer: "We offer multiple professional templates including Gusto-style, Workday-style, and custom templates. Each is designed to match the look of paystubs from major payroll providers, giving you professional documentation regardless of your employer setup."
    },
    {
      question: "Is my information secure when using MintSlip?",
      answer: "Your privacy is our priority. We use secure PayPal payment processing and do not store your personal information after your document is generated. Your data is processed in real-time and immediately discarded after your paystub is created."
    }
  ];

  const internalLinks = [
    {
      title: "Pay Stub Generator",
      description: "Create professional paystubs instantly with accurate tax calculations",
      path: "/app",
      icon: FileText
    },
    {
      title: "W-2 Form Generator",
      description: "Generate IRS-compliant W-2 forms for tax filing",
      path: "/app/tax-forms",
      icon: Receipt
    },
    {
      title: "1099-NEC Generator",
      description: "Create 1099 forms for contractor and freelance payments",
      path: "/app/tax-forms",
      icon: FileBarChart
    },
    {
      title: "Canadian Paystub Generator",
      description: "Generate paystubs with Canadian tax calculations",
      path: "/app/canadian-paystub",
      icon: Building2
    },
    {
      title: "Self-Employed Paystub Generator",
      description: "Documentation for freelancers and self-employed individuals",
      path: "/app/paystubs",
      icon: Briefcase
    },
    {
      title: "Contractor Paystub Generator",
      description: "Create payment statements for 1099 contractors",
      path: "/app/paystubs",
      icon: User
    },
    {
      title: "Accounting Mockups",
      description: "Professional financial statement templates",
      path: "/app/business-forms",
      icon: Calculator
    },
    {
      title: "Paystub Samples & Templates",
      description: "Browse our collection of professional paystub templates",
      path: "/paystub-samples",
      icon: FileText
    }
  ];

  // Schema.org structured data for SEO
  const schemaData = {
    "@context": "https://schema.org",
    "@type": "HowTo",
    "name": "How to Make a Paystub",
    "description": "Learn how to create professional paystubs in minutes using MintSlip's online paystub generator. Step-by-step guide with templates.",
    "image": "https://mintslip.com/og-image.png",
    "totalTime": "PT5M",
    "estimatedCost": {
      "@type": "MonetaryAmount",
      "currency": "USD",
      "value": "8.99"
    },
    "step": [
      {
        "@type": "HowToStep",
        "name": "Choose Your Template",
        "text": "Select from professional paystub templates including Gusto and Workday styles",
        "position": 1
      },
      {
        "@type": "HowToStep",
        "name": "Enter Your Information",
        "text": "Fill in employer details, employee information, pay period, earnings, and deductions",
        "position": 2
      },
      {
        "@type": "HowToStep",
        "name": "Review and Download",
        "text": "Preview your paystub, make any adjustments, then download the professional PDF",
        "position": 3
      }
    ]
  };

  // FAQ Schema for SEO
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer.replace(/&apos;/g, "'")
      }
    }))
  };

  return (
    <div className="min-h-screen bg-white relative" style={{ overflowX: "clip" }}>
      <Helmet>
        <title>How to Make a Paystub | Step-by-Step Guide 2025 | MintSlip</title>
        <meta name="description" content="Learn how to make a paystub in minutes with our step-by-step guide. Create professional pay stubs with accurate tax calculations. Free preview, instant download. Trusted by 1,000+ users." />
        <meta name="keywords" content="how to make a paystub, create paystub, paystub generator, pay stub maker, free paystub template, make pay stub online, paystub creator, generate paystub" />
        <meta property="og:title" content="How to Make a Paystub - Complete Step-by-Step Guide 2025" />
        <meta property="og:description" content="Create professional paystubs in under 5 minutes. Our guide shows you exactly how to make accurate pay stubs with proper tax calculations." />
        <meta property="og:type" content="article" />
        <meta property="og:url" content="https://mintslip.com/how-to-make-a-paystub" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="How to Make a Paystub | MintSlip" />
        <meta name="twitter:description" content="Step-by-step guide to creating professional paystubs with accurate tax calculations." />
        <link rel="canonical" href="https://mintslip.com/how-to-make-a-paystub" />
        <script type="application/ld+json">
          {JSON.stringify(schemaData)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(faqSchema)}
        </script>
      </Helmet>

      <Header />

      {/* Hero — centered like the home hero, with the stats bento below */}
      <section ref={heroRef} className="relative max-w-[1288px] mx-auto px-6 pt-14 pb-16 md:pt-20 md:pb-20">
        <div className={`transition-all duration-700 ${heroInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          {/* Breadcrumb */}
          <nav className="mb-6 text-sm">
            <ol className="flex items-center justify-center gap-2 text-slate-500">
              <li><Link to="/" className="hover:text-emerald-700 transition-colors">Home</Link></li>
              <li>/</li>
              <li className="text-slate-900 font-medium">How to Make a Paystub</li>
            </ol>
          </nav>

          <div className="flex justify-center">
            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 bg-emerald-50 rounded-full border border-emerald-200/80 mb-6">
              <Star className="w-4 h-4 text-yellow-400 fill-yellow-400" />
              <span className="text-sm font-medium text-emerald-900">Updated for 2025 Tax Rates</span>
            </div>
          </div>

          <h1 className="font-display text-center text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight text-slate-900 mb-5" style={{ lineHeight: 1.08 }}>
            How to make a <span className="font-black">paystub</span>
          </h1>
          <p className="text-center text-lg md:text-xl leading-relaxed text-slate-600 max-w-2xl mx-auto mb-8">
            Creating professional pay stubs has never been easier. Follow our step-by-step guide to generate accurate paystubs with proper tax calculations in under 5 minutes.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-14">
            <button className="navbar-cta" onClick={() => navigate("/app/paystubs")}>
              <FileText size={18} />
              <span>Create a paystub</span>
            </button>
            <button
              onClick={() => navigate("/paystub-samples")}
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-slate-300 text-slate-700 font-medium hover:bg-slate-50 transition-colors"
            >
              View Sample Templates
            </button>
          </div>

          {/* Quick stats bento */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 md:gap-5 max-w-3xl mx-auto">
            {HERO_STATS.map((s) => {
              const t = toneCard[s.tone];
              return (
                <div key={s.label} className={`rounded-[28px] ${t.bg} px-4 py-8 md:px-8 md:py-10 flex flex-col items-center justify-center text-center`}>
                  <span className={`font-display font-black text-4xl md:text-5xl ${t.big}`}>{s.value}</span>
                  <span className={`mt-2 text-sm md:text-base ${t.small}`}>{s.label}</span>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* What is a Paystub Section */}
      <section className="max-w-[1288px] mx-auto px-6 py-16 md:py-20">
        <h2 className="font-display text-center text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight text-slate-900 mb-10">
          What is a <span className="font-black">paystub</span> and why do you need one?
        </h2>

        <div className="max-w-3xl mx-auto">
          <p className="text-slate-600 text-lg leading-relaxed mb-6">
            A <strong>paystub</strong> (also called a pay stub, paycheck stub, or earnings statement) is a document that itemizes your earnings for a specific pay period. It shows your gross pay, deductions for taxes and benefits, and your net (take-home) pay. Whether you&apos;re an employee, freelancer, or small business owner, understanding how to make a paystub is essential for financial documentation.
          </p>

          <p className="text-slate-600 text-lg leading-relaxed mb-8">
            Paystubs serve multiple important purposes in today&apos;s financial landscape. For employees, they provide a clear breakdown of earnings and deductions, helping you verify that your pay is correct and understand where your money goes. For self-employed individuals and contractors, paystubs serve as proof of income for loan applications, apartment rentals, and other financial transactions.
          </p>

          <div className="rounded-[28px] bg-emerald-400 p-8 md:p-10 mb-6">
            <h3 className="font-display text-2xl md:text-3xl font-medium text-slate-900 mb-5">Common uses for paystubs</h3>
            <ul className="grid grid-cols-1 md:grid-cols-2 gap-3 text-emerald-950">
              <li className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-emerald-950 flex-shrink-0" />
                <span>Apartment rental applications</span>
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-emerald-950 flex-shrink-0" />
                <span>Mortgage and loan documentation</span>
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-emerald-950 flex-shrink-0" />
                <span>Tax preparation and filing</span>
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-emerald-950 flex-shrink-0" />
                <span>Proof of employment or income</span>
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-emerald-950 flex-shrink-0" />
                <span>Child support and income verification</span>
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-emerald-950 flex-shrink-0" />
                <span>Government benefit eligibility documentation</span>
              </li>
            </ul>
          </div>

          <p className="text-sm text-slate-500 italic">
            <strong>Important:</strong> MintSlip is intended for lawful and legitimate use only. Users are responsible for ensuring all information entered is accurate and compliant with applicable laws and requirements.
          </p>
        </div>
      </section>

      {/* Detailed Step-by-Step Guide */}
      <section ref={stepsRef} className="max-w-[1288px] mx-auto px-6 py-16 md:py-20">
        <div className={`transition-all duration-700 ${stepsInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
          <h2 className="font-display text-center text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight text-slate-900 mb-6">
            Step-by-step guide: how to <span className="font-black">make a paystub</span>
          </h2>
          <p className="text-center text-lg md:text-xl text-slate-600 max-w-2xl mx-auto mb-14">
            Follow these three simple steps to create a professional paystub with MintSlip. Our platform handles all the complex calculations automatically.
          </p>

          {/* Step 1 - Detailed */}
          <div className="mb-12 md:mb-16">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8 items-center">
              <div className="order-2 lg:order-1">
                <div className="rounded-[28px] bg-slate-100 p-8 md:p-10">
                  <div className="flex items-center gap-4 mb-6">
                    <span className="w-14 h-14 rounded-full bg-white shadow-sm flex items-center justify-center font-display font-black text-2xl text-slate-900 flex-shrink-0">1</span>
                    <h3 className="font-display text-2xl md:text-3xl font-medium text-slate-900">Choose your paystub template</h3>
                  </div>

                  <p className="text-slate-600 text-lg leading-relaxed mb-6">
                    Start by selecting a professional paystub template that matches your needs. MintSlip offers several industry-standard formats:
                  </p>

                  <ul className="space-y-4 mb-6">
                    <li className="flex items-start gap-3">
                      <span className="w-6 h-6 rounded-full bg-white shadow-sm flex items-center justify-center mt-0.5 flex-shrink-0">
                        <CheckCircle className="w-4 h-4 text-emerald-600" />
                      </span>
                      <div>
                        <strong className="text-slate-900">Gusto-Style Template</strong>
                        <p className="text-sm text-slate-500">Clean, modern design preferred by tech companies and startups</p>
                      </div>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="w-6 h-6 rounded-full bg-white shadow-sm flex items-center justify-center mt-0.5 flex-shrink-0">
                        <CheckCircle className="w-4 h-4 text-emerald-600" />
                      </span>
                      <div>
                        <strong className="text-slate-900">Workday-Style Template</strong>
                        <p className="text-sm text-slate-500">Enterprise-grade format with comprehensive details</p>
                      </div>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="w-6 h-6 rounded-full bg-white shadow-sm flex items-center justify-center mt-0.5 flex-shrink-0">
                        <CheckCircle className="w-4 h-4 text-emerald-600" />
                      </span>
                      <div>
                        <strong className="text-slate-900">Custom Template</strong>
                        <p className="text-sm text-slate-500">Personalize colors and branding to match your company</p>
                      </div>
                    </li>
                  </ul>

                  <div className="bg-white rounded-2xl p-5">
                    <p className="text-sm text-slate-600">
                      <strong className="text-slate-900">Pro Tip:</strong> Choose a template that matches your employer&apos;s actual payroll provider for the most authentic appearance.
                    </p>
                  </div>
                </div>
              </div>

              <div className="order-1 lg:order-2">
                <div className="rounded-[28px] bg-white border border-slate-200 shadow-sm p-5 md:p-6">
                  <div className="relative">
                    <div className="absolute -top-3 -left-3 z-10">
                      <span className="bg-emerald-500 text-white font-bold px-4 py-1.5 rounded-full text-sm shadow-lg flex items-center gap-2">
                        <MousePointer className="w-4 h-4" />
                        STEP 1
                      </span>
                    </div>
                    <div className="bg-slate-100 rounded-2xl overflow-hidden">
                      <img src={select} alt="Step 1: Select a professional paystub template from MintSlip's template library" className="w-full h-auto" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Step 2 - Detailed */}
          <div className="mb-12 md:mb-16">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8 items-center">
              <div>
                <div className="rounded-[28px] bg-white border border-slate-200 shadow-sm p-5 md:p-6">
                  <div className="relative">
                    <div className="absolute -top-3 -left-3 z-10">
                      <span className="bg-emerald-500 text-white font-bold px-4 py-1.5 rounded-full text-sm shadow-lg flex items-center gap-2">
                        <Edit3 className="w-4 h-4" />
                        STEP 2
                      </span>
                    </div>
                    <div className="bg-slate-100 rounded-2xl overflow-hidden">
                      <img src={inputInfo} alt="Step 2: Enter your employer information, employee details, earnings, and deductions" className="w-full h-auto" />
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <div className="rounded-[28px] bg-emerald-400 p-8 md:p-10">
                  <div className="flex items-center gap-4 mb-6">
                    <span className="w-14 h-14 rounded-full bg-white shadow-sm flex items-center justify-center font-display font-black text-2xl text-slate-900 flex-shrink-0">2</span>
                    <h3 className="font-display text-2xl md:text-3xl font-medium text-slate-900">Enter your information</h3>
                  </div>

                  <p className="text-emerald-950 text-lg leading-relaxed mb-6">
                    Fill in your details in our intuitive form. Our smart system automatically calculates taxes and deductions based on current rates. Here&apos;s what you&apos;ll need:
                  </p>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                    <div className="bg-white rounded-2xl p-4">
                      <h4 className="font-bold text-slate-900 mb-2 flex items-center gap-2">
                        <Building2 className="w-4 h-4 text-emerald-600" />
                        Employer Info
                      </h4>
                      <ul className="text-sm text-slate-600 space-y-1">
                        <li>• Company name</li>
                        <li>• Business address</li>
                        <li>• EIN (optional)</li>
                      </ul>
                    </div>

                    <div className="bg-white rounded-2xl p-4">
                      <h4 className="font-bold text-slate-900 mb-2 flex items-center gap-2">
                        <User className="w-4 h-4 text-emerald-600" />
                        Employee Info
                      </h4>
                      <ul className="text-sm text-slate-600 space-y-1">
                        <li>• Full name</li>
                        <li>• Address</li>
                        <li>• SSN (last 4 digits)</li>
                      </ul>
                    </div>

                    <div className="bg-white rounded-2xl p-4">
                      <h4 className="font-bold text-slate-900 mb-2 flex items-center gap-2">
                        <DollarSign className="w-4 h-4 text-emerald-600" />
                        Earnings
                      </h4>
                      <ul className="text-sm text-slate-600 space-y-1">
                        <li>• Hourly rate or salary</li>
                        <li>• Hours worked</li>
                        <li>• Overtime hours</li>
                      </ul>
                    </div>

                    <div className="bg-white rounded-2xl p-4">
                      <h4 className="font-bold text-slate-900 mb-2 flex items-center gap-2">
                        <Calendar className="w-4 h-4 text-emerald-600" />
                        Pay Period
                      </h4>
                      <ul className="text-sm text-slate-600 space-y-1">
                        <li>• Start date</li>
                        <li>• End date</li>
                        <li>• Pay date</li>
                      </ul>
                    </div>
                  </div>

                  <div className="bg-white rounded-2xl p-5">
                    <p className="text-sm text-slate-600">
                      <strong className="text-slate-900">Auto-Calculation:</strong> Our system automatically calculates federal income tax, Social Security, Medicare, and state taxes based on current 2025 tax rates.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Step 3 - Detailed */}
          <div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8 items-center">
              <div className="order-2 lg:order-1">
                <div className="rounded-[28px] bg-[#0b0b0b] p-8 md:p-10">
                  <div className="flex items-center gap-4 mb-6">
                    <span className="w-14 h-14 rounded-full bg-white shadow-sm flex items-center justify-center font-display font-black text-2xl text-slate-900 flex-shrink-0">3</span>
                    <h3 className="font-display text-2xl md:text-3xl font-medium text-white">Preview, pay &amp; download</h3>
                  </div>

                  <p className="text-slate-300 text-lg leading-relaxed mb-6">
                    Before finalizing, you&apos;ll see a live preview of your paystub with a MintSlip watermark. This lets you verify all information is correct. Once satisfied:
                  </p>

                  <ol className="space-y-4 mb-6">
                    <li className="flex items-start gap-3">
                      <span className="w-6 h-6 rounded-full bg-emerald-500 text-white flex items-center justify-center text-sm font-bold mt-0.5 flex-shrink-0">1</span>
                      <div>
                        <strong className="text-white">Review Your Preview</strong>
                        <p className="text-sm text-slate-300">Check all details are accurate, including earnings, taxes, and deductions</p>
                      </div>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="w-6 h-6 rounded-full bg-emerald-500 text-white flex items-center justify-center text-sm font-bold mt-0.5 flex-shrink-0">2</span>
                      <div>
                        <strong className="text-white">Secure PayPal Payment</strong>
                        <p className="text-sm text-slate-300">Pay securely via PayPal - we never see your financial information</p>
                      </div>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="w-6 h-6 rounded-full bg-emerald-500 text-white flex items-center justify-center text-sm font-bold mt-0.5 flex-shrink-0">3</span>
                      <div>
                        <strong className="text-white">Instant PDF Download</strong>
                        <p className="text-sm text-slate-300">Your professional paystub downloads immediately - no watermark</p>
                      </div>
                    </li>
                  </ol>

                  <div className="flex gap-3 mb-8">
                    <div className="flex-1 bg-white/10 rounded-2xl p-3 text-center">
                      <Shield className="w-6 h-6 text-emerald-400 mx-auto mb-1" />
                      <p className="text-xs text-slate-300 font-medium">Secure Payment</p>
                    </div>
                    <div className="flex-1 bg-white/10 rounded-2xl p-3 text-center">
                      <Clock className="w-6 h-6 text-emerald-400 mx-auto mb-1" />
                      <p className="text-xs text-slate-300 font-medium">Instant Download</p>
                    </div>
                    <div className="flex-1 bg-white/10 rounded-2xl p-3 text-center">
                      <FileText className="w-6 h-6 text-emerald-400 mx-auto mb-1" />
                      <p className="text-xs text-slate-300 font-medium">PDF Format</p>
                    </div>
                  </div>

                  <button className="navbar-cta" onClick={() => navigate("/app/paystubs")}>
                    <Zap size={18} />
                    <span>Start Creating Your Paystub</span>
                  </button>
                </div>
              </div>

              <div className="order-1 lg:order-2">
                <div className="rounded-[28px] bg-white border border-slate-200 shadow-sm p-5 md:p-6">
                  <div className="relative">
                    <div className="absolute -top-3 -left-3 z-10">
                      <span className="bg-emerald-500 text-white font-bold px-4 py-1.5 rounded-full text-sm shadow-lg flex items-center gap-2">
                        <Download className="w-4 h-4" />
                        STEP 3
                      </span>
                    </div>
                    <div className="bg-slate-100 rounded-2xl overflow-hidden">
                      <img src={download} alt="Step 3: Preview your paystub, pay securely via PayPal, and download your PDF instantly" className="w-full h-auto" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Template Examples Section */}
      <section ref={templatesRef} className="max-w-[1288px] mx-auto px-6 py-16 md:py-20">
        <h2 className={`font-display text-center text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight text-slate-900 mb-6 transition-all duration-700 ${templatesInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          Professional <span className="font-black">paystub templates</span>
        </h2>
        <p className={`text-center text-lg md:text-xl text-slate-600 max-w-2xl mx-auto mb-12 transition-all duration-700 delay-100 ${templatesInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          Choose from industry-standard templates that match the format used by major payroll providers. Each template includes accurate tax calculations and YTD tracking.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
          {TEMPLATES.map((template, i) => (
            <div
              key={template.id}
              className={`transition-all duration-700 ${templatesInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}
              style={{ transitionDelay: `${150 + i * 120}ms` }}
            >
              <TemplatePreviewCard
                template={template}
                previewImage={previews[template.id]}
                isLoading={loading[template.id]}
                onUseTemplate={handleUseTemplate}
              />
            </div>
          ))}
        </div>

        <div className="text-center mt-10">
          <button
            onClick={() => navigate("/paystub-samples")}
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-slate-300 text-slate-700 font-medium hover:bg-slate-50 transition-colors"
          >
            View All Templates
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </section>

      {/* Long-form SEO Content */}
      <section className="max-w-[1288px] mx-auto px-6 py-16 md:py-20">
        <h2 className="font-display text-center text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight text-slate-900 mb-10">
          Complete guide to making <span className="font-black">professional paystubs</span>
        </h2>

        <article className="max-w-3xl mx-auto">
          <h3 className="font-display text-2xl md:text-3xl font-medium text-slate-900 mt-8 mb-4">Understanding paystub components</h3>

          <p className="text-slate-600 text-lg leading-relaxed mb-4">
            Before you learn how to make a paystub, it&apos;s important to understand what information a professional pay stub contains. Every paystub, regardless of the template or employer, includes several key sections that break down your compensation and deductions.
          </p>

          <p className="text-slate-600 text-lg leading-relaxed mb-4">
            <strong>Gross Pay</strong> represents your total earnings before any deductions. For hourly employees, this is calculated by multiplying your hourly rate by the number of hours worked. Salaried employees see their gross pay as a portion of their annual salary divided by the number of pay periods. Understanding your gross pay is the foundation of reading any paystub.
          </p>

          <p className="text-slate-600 text-lg leading-relaxed mb-4">
            <strong>Federal Income Tax</strong> is withheld based on the information you provided on your W-4 form. The amount withheld depends on your filing status, number of dependents, and whether you&apos;ve elected additional withholding. MintSlip&apos;s paystub generator uses current IRS tax tables to calculate accurate federal withholding amounts.
          </p>

          <p className="text-slate-600 text-lg leading-relaxed mb-4">
            <strong>FICA Taxes</strong> include Social Security (6.2% of gross pay up to the annual wage base) and Medicare (1.45% of all gross pay). These taxes fund federal retirement and healthcare programs. High earners may also see Additional Medicare Tax of 0.9% on wages exceeding $200,000.
          </p>

          <h3 className="font-display text-2xl md:text-3xl font-medium text-slate-900 mt-10 mb-4">State and local tax considerations</h3>

          <p className="text-slate-600 text-lg leading-relaxed mb-4">
            <strong>State Income Tax</strong> varies significantly by location. Some states like Texas, Florida, and Nevada have no state income tax, while others like California and New York have progressive tax rates that can exceed 10% for high earners. When you create a paystub with MintSlip, our system automatically applies the correct state tax rate based on your location.
          </p>

          <p className="text-slate-600 text-lg leading-relaxed mb-4">
            <strong>Local Taxes</strong> are required in certain cities and counties. For example, New York City residents pay an additional city income tax, and some Ohio cities have local income taxes. Always verify your local tax obligations when creating paystubs to ensure accuracy.
          </p>

          <h3 className="font-display text-2xl md:text-3xl font-medium text-slate-900 mt-10 mb-4">Pre-tax vs. post-tax deductions</h3>

          <p className="text-slate-600 text-lg leading-relaxed mb-4">
            <strong>Pre-tax deductions</strong> reduce your taxable income before federal and state taxes are calculated. Common pre-tax deductions include 401(k) contributions, health insurance premiums, Health Savings Account (HSA) contributions, and Flexible Spending Account (FSA) contributions. These deductions lower your tax liability while still providing valuable benefits.
          </p>

          <p className="text-slate-600 text-lg leading-relaxed mb-4">
            <strong>Post-tax deductions</strong> are taken from your pay after taxes have been calculated. Examples include Roth 401(k) contributions, life insurance premiums that exceed certain limits, union dues, and garnishments. Understanding the difference between pre-tax and post-tax deductions helps you accurately interpret your paystub.
          </p>

          <h3 className="font-display text-2xl md:text-3xl font-medium text-slate-900 mt-10 mb-4">Year-to-date (YTD) tracking</h3>

          <p className="text-slate-600 text-lg leading-relaxed mb-4">
            Every professional paystub includes year-to-date totals showing your cumulative earnings and deductions from January 1st through the current pay period. YTD information is crucial for:
          </p>

          <ul className="list-disc pl-6 mb-4 text-slate-600 text-lg leading-relaxed space-y-2">
            <li>Tracking progress toward Social Security wage base limits</li>
            <li>Monitoring 401(k) contribution limits ($23,000 in 2025)</li>
            <li>Verifying your income for loan applications</li>
            <li>Preparing for tax season by knowing your total earnings</li>
            <li>Budgeting and financial planning throughout the year</li>
          </ul>

          <h3 className="font-display text-2xl md:text-3xl font-medium text-slate-900 mt-10 mb-4">Paystubs for different worker types</h3>

          <p className="text-slate-600 text-lg leading-relaxed mb-4">
            <strong>W-2 Employees</strong> receive the most comprehensive paystubs with all taxes automatically withheld. Employers are legally required to withhold federal, state, and FICA taxes from each paycheck. If you&apos;re a W-2 employee needing documentation, MintSlip creates authentic-looking paystubs that match what major employers provide.
          </p>

          <p className="text-slate-600 text-lg leading-relaxed mb-4">
            <strong>1099 Contractors</strong> are responsible for their own taxes, so their payment statements don&apos;t include tax withholdings. Instead, contractors receive the full payment amount and must make quarterly estimated tax payments. MintSlip offers a contractor paystub option that clearly shows the payment without misleading tax withholding information.
          </p>

          <p className="text-slate-600 text-lg leading-relaxed mb-4">
            <strong>Self-Employed Individuals</strong> often need to create paystubs as proof of income even though they don&apos;t receive traditional paychecks. Whether you&apos;re a freelancer, gig worker, or small business owner, MintSlip helps you document your earnings professionally for mortgage applications, rental agreements, or other financial purposes.
          </p>

          <h3 className="font-display text-2xl md:text-3xl font-medium text-slate-900 mt-10 mb-4">Best practices for creating paystubs</h3>

          <p className="text-slate-600 text-lg leading-relaxed mb-4">
            When learning how to make a paystub, accuracy is paramount. Here are essential best practices to follow:
          </p>

          <ol className="list-decimal pl-6 mb-4 text-slate-600 text-lg leading-relaxed space-y-3">
            <li><strong>Double-check all personal information</strong> - Names, addresses, and identification numbers must be accurate</li>
            <li><strong>Use correct pay period dates</strong> - The pay period should accurately reflect the dates you&apos;re documenting</li>
            <li><strong>Verify hourly rates or salary</strong> - Ensure your compensation matches your actual agreement</li>
            <li><strong>Account for overtime correctly</strong> - Overtime is typically paid at 1.5x your regular rate</li>
            <li><strong>Include all relevant deductions</strong> - Don&apos;t forget health insurance, retirement contributions, or other regular deductions</li>
            <li><strong>Keep records</strong> - Save copies of all paystubs for tax purposes and financial documentation</li>
          </ol>

          <div className="rounded-[28px] bg-emerald-400 p-8 md:p-10 my-10">
            <h4 className="font-display text-2xl md:text-3xl font-medium text-slate-900 mb-3">Ready to create your paystub?</h4>
            <p className="text-emerald-950 leading-relaxed mb-6">
              MintSlip makes it easy to create professional, accurate paystubs in under 5 minutes. Our platform handles all the complex tax calculations so you can focus on what matters.
            </p>
            <button className="navbar-cta" onClick={() => navigate("/app/paystubs")}>
              <FileText size={18} />
              <span>Create Your Paystub Now</span>
            </button>
          </div>
        </article>
      </section>

      {/* FAQ Section */}
      <section ref={faqRef} className="max-w-[1288px] mx-auto px-6 py-16 md:py-20">
        <h2 className={`font-display text-center text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight text-slate-900 mb-6 transition-all duration-700 ${faqInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          Frequently asked <span className="font-black">questions</span>
        </h2>
        <p className={`text-center text-lg md:text-xl text-slate-600 max-w-2xl mx-auto mb-12 transition-all duration-700 delay-100 ${faqInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          Common questions about creating paystubs with MintSlip
        </p>

        <div className="max-w-3xl mx-auto">
          {faqs.map((faq, index) => (
            <FAQItem
              key={index}
              question={faq.question}
              answer={faq.answer}
              isOpen={openFAQ === index}
              onClick={() => setOpenFAQ(openFAQ === index ? -1 : index)}
            />
          ))}

          <div className="text-center mt-8">
            <button
              onClick={() => navigate("/faq")}
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-slate-300 text-slate-700 font-medium hover:bg-slate-50 transition-colors"
            >
              <HelpCircle className="w-4 h-4" />
              View All FAQs
            </button>
          </div>
        </div>
      </section>

      {/* Internal Links Section */}
      <section ref={linksRef} className="max-w-[1288px] mx-auto px-6 py-16 md:py-20">
        <h2 className={`font-display text-center text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight text-slate-900 mb-6 transition-all duration-700 ${linksInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          Explore more <span className="font-black">document generators</span>
        </h2>
        <p className={`text-center text-lg md:text-xl text-slate-600 max-w-2xl mx-auto mb-12 transition-all duration-700 delay-100 ${linksInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          MintSlip offers a complete suite of professional document generators for all your financial documentation needs.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {internalLinks.map((link, idx) => (
            <div
              key={idx}
              className={`transition-all duration-700 ${linksInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}
              style={{ transitionDelay: `${150 + idx * 60}ms` }}
            >
              <InternalLinkCard {...link} />
            </div>
          ))}
        </div>
      </section>

      {/* Final CTA Section */}
      <section ref={ctaRef} className="pb-20 md:pb-28 px-6 bg-white pt-8">
        <div className={`max-w-[1288px] mx-auto text-center transition-all duration-700 ${ctaInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight text-slate-900 mb-5" style={{ lineHeight: 1.08 }}>
            Ready to create your <span className="font-black">professional paystub</span>?
          </h2>
          <p className="text-lg md:text-xl leading-relaxed text-slate-600 max-w-2xl mx-auto mb-8">
            Join over {userCount} satisfied users who trust MintSlip for accurate, professional pay stubs. Create yours in under 5 minutes with our easy-to-use generator.
          </p>

          <div className="flex justify-center mb-4">
            <button className="navbar-cta" onClick={() => navigate("/app/paystubs")}>
              <FileText size={18} />
              <span>Create a paystub</span>
            </button>
          </div>
          <button
            type="button"
            onClick={() => openSupportChat()}
            className="text-slate-600 underline underline-offset-4 hover:text-emerald-700 transition-colors"
          >
            Contact Support
          </button>

          <div className="flex flex-wrap items-center justify-center gap-8 mt-12 text-slate-500 text-sm">
            <div className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-emerald-700" />
              <span>Secure Payment</span>
            </div>
            <div className="flex items-center gap-2">
              <Clock className="w-5 h-5 text-emerald-700" />
              <span>Instant Download</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-emerald-700" />
              <span>No Account Required</span>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
